const DEFAULT_REDIRECT = '/';
const REDIRECT_PARAM = 'redirect_url';
const MAX_REDIRECT_LENGTH = 2048;
const AUTH_ROUTE_PREFIXES = ['/sign-in', '/sign-up'];
const UNICODE_SEPARATOR_PATTERN = /[\u2044\u2215\u29f5\u29f8\ufe68\uff0f\uff3c]/u;
const ENCODED_DELIMITER_PATTERN = /%(?:25|2f|5c|0[0-9a-f]|1[0-9a-f]|7f)/i;
const MALFORMED_PERCENT_PATTERN = /%(?![0-9a-f]{2})/i;

const resolveOrigin = (origin) => {
  const suppliedOrigin = origin || (typeof window !== 'undefined' ? window.location.origin : 'http://localhost');
  return new URL(suppliedOrigin).origin;
};

const invalidRedirect = (reason) => ({
  isValid: false,
  value: DEFAULT_REDIRECT,
  reason
});

const containsControlCharacter = (value) => Array.from(value).some((character) => {
  const codePoint = character.codePointAt(0);
  return codePoint <= 0x1f || codePoint === 0x7f;
});

/**
 * Validate an authentication return target as a same-origin application path.
 *
 * date_of_change: 2026-09-14
 * security_reference: Auth return values cross a trust boundary before Clerk and React Router consume them.
 * Reject ambiguous separators and encoded delimiters instead of relying on downstream URL interpretation.
 */
export const assessLocalRedirect = (value, { origin } = {}) => {
  if (typeof value !== 'string' || value.length === 0) {
    return invalidRedirect('missing');
  }
  if (value.length > MAX_REDIRECT_LENGTH) {
    return invalidRedirect('too_long');
  }
  if (value !== value.trim()) {
    return invalidRedirect('surrounding_whitespace');
  }
  if (containsControlCharacter(value)) {
    return invalidRedirect('control_character');
  }
  if (value.includes('\\')) {
    return invalidRedirect('backslash');
  }
  if (UNICODE_SEPARATOR_PATTERN.test(value)) {
    return invalidRedirect('unicode_separator');
  }
  if (ENCODED_DELIMITER_PATTERN.test(value)) {
    return invalidRedirect('encoded_delimiter');
  }
  if (MALFORMED_PERCENT_PATTERN.test(value)) {
    return invalidRedirect('malformed_encoding');
  }
  if (!value.startsWith('/') || value.startsWith('//')) {
    return invalidRedirect('not_local_path');
  }

  try {
    const trustedOrigin = resolveOrigin(origin);
    const resolved = new URL(value, `${trustedOrigin}/`);
    const canonicalValue = `${resolved.pathname}${resolved.search}${resolved.hash}`;

    if (
      resolved.origin !== trustedOrigin
      || resolved.username
      || resolved.password
      || !canonicalValue.startsWith('/')
      || canonicalValue.startsWith('//')
      || canonicalValue.includes('\\')
    ) {
      return invalidRedirect('origin_mismatch');
    }

    return {
      isValid: true,
      value: canonicalValue,
      reason: null
    };
  } catch (_error) {
    return invalidRedirect('invalid_url');
  }
};

export const getSafeLocalRedirect = (value, options) => assessLocalRedirect(value, options).value;

export const getRedirectFromSearch = (search, options) => {
  const params = new URLSearchParams(search || '');
  const candidates = params.getAll(REDIRECT_PARAM);
  if (candidates.length !== 1) {
    return DEFAULT_REDIRECT;
  }
  return getSafeLocalRedirect(candidates[0], options);
};

export const buildLocationRedirectTarget = (location, options) => getSafeLocalRedirect(
  `${location?.pathname || ''}${location?.search || ''}${location?.hash || ''}`,
  options
);

export const buildAuthRoute = (pathname, redirectValue, options) => {
  if (!AUTH_ROUTE_PREFIXES.includes(pathname)) {
    throw new Error(`Unsupported authentication route: ${pathname}`);
  }
  const params = new URLSearchParams();
  params.set(REDIRECT_PARAM, getSafeLocalRedirect(redirectValue, options));
  return `${pathname}?${params.toString()}`;
};

const isAuthPath = (pathname) => AUTH_ROUTE_PREFIXES.some(
  (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`)
);

/**
 * Replace unsafe auth query input before React renders and before Clerk initializes.
 * The replacement is always same-origin and preserves unrelated Clerk query parameters.
 *
 * date_of_change: 2026-09-14
 * security_reference: Component-level redirect props are too late to protect provider initialization.
 */
export const preflightAuthRedirect = (browserWindow) => {
  if (!browserWindow?.location?.href || !browserWindow?.history?.replaceState) {
    return { safeToRender: false, changed: false, redirect: DEFAULT_REDIRECT, reason: 'browser_unavailable' };
  }

  try {
    const currentUrl = new URL(browserWindow.location.href);
    if (!isAuthPath(currentUrl.pathname)) {
      return { safeToRender: true, changed: false, redirect: DEFAULT_REDIRECT, reason: 'not_auth_route' };
    }

    const candidates = currentUrl.searchParams.getAll(REDIRECT_PARAM);
    if (candidates.length === 0) {
      return { safeToRender: true, changed: false, redirect: DEFAULT_REDIRECT, reason: 'missing' };
    }

    const assessment = candidates.length === 1
      ? assessLocalRedirect(candidates[0], { origin: currentUrl.origin })
      : invalidRedirect('repeated_parameter');
    const requiresReplacement = candidates.length !== 1 || !assessment.isValid || assessment.value !== candidates[0];

    if (!requiresReplacement) {
      return { safeToRender: true, changed: false, redirect: assessment.value, reason: null };
    }

    currentUrl.searchParams.delete(REDIRECT_PARAM);
    currentUrl.searchParams.set(REDIRECT_PARAM, assessment.value);
    const safeRelativeUrl = `${currentUrl.pathname}${currentUrl.search}${currentUrl.hash}`;
    browserWindow.history.replaceState(browserWindow.history.state, '', safeRelativeUrl);

    return {
      safeToRender: true,
      changed: true,
      redirect: assessment.value,
      reason: assessment.reason || 'canonicalized'
    };
  } catch (_error) {
    return { safeToRender: false, changed: false, redirect: DEFAULT_REDIRECT, reason: 'preflight_failed' };
  }
};

export const getCurrentOriginAllowlist = (browserWindow) => {
  try {
    return [resolveOrigin(browserWindow?.location?.origin)];
  } catch (_error) {
    return [];
  }
};

/**
 * Keep Clerk's protocol validation aligned with the already-validated browser
 * origin instead of maintaining a second, broader environment allowlist.
 * date_of_change: 2026-09-14
 */
export const getCurrentProtocolAllowlist = (browserWindow) => {
  try {
    return [new URL(resolveOrigin(browserWindow?.location?.origin)).protocol];
  } catch (_error) {
    return [];
  }
};

export { DEFAULT_REDIRECT, REDIRECT_PARAM };
